import type { AskQueryEvent, ReportSSEEvent } from "@unbox/api";
import { client, type MerchantScope } from "@/api/scoped-merchant-client";

export type QueryBody = {
  question: string;
  conversation_id?: string | null;
  session_id?: string;
};

export type ReportBody = {
  templateId: string;
  additionalInputs?: Record<string, string>;
  session_id?: string;
};

type Frame = { event: string; data: unknown };

export async function streamQuery(
  scope: MerchantScope,
  body: QueryBody,
  onEvent: (event: AskQueryEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const init = { init: { signal } };
  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].ask.query.$post(
        { param: scope.param, json: body },
        init,
      )
    : await client.merchant[":merchantId"].ask.query.$post({ param: scope.param, json: body }, init);
  await readFrames(res, (frame) => onEvent(frame as AskQueryEvent));
}

export async function streamReport(
  scope: MerchantScope,
  body: ReportBody,
  onEvent: (event: ReportSSEEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const init = { init: { signal } };
  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].ask.report.$post(
        { param: scope.param, json: body },
        init,
      )
    : await client.merchant[":merchantId"].ask.report.$post({ param: scope.param, json: body }, init);
  await readFrames(res, (frame) => onEvent({ type: frame.event, data: frame.data } as ReportSSEEvent));
}

/** Parses SSE frames (event: / data: lines separated by a blank line) as they arrive. */
async function readFrames(
  res: { ok: boolean; status: number; body: ReadableStream<Uint8Array> | null },
  onFrame: (frame: Frame) => void,
): Promise<void> {
  if (!res.ok || !res.body) throw new Error(`Server error: ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const parse = (chunk: string): Frame[] => {
    buffer += chunk;
    const frames: Frame[] = [];
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const part of parts) {
      let event = "message";
      let dataStr = "";
      for (const line of part.split("\n")) {
        if (line.startsWith("event: ")) event = line.slice(7).trim();
        else if (line.startsWith("data: ")) dataStr = dataStr ? `${dataStr}\n${line.slice(6)}` : line.slice(6);
      }
      if (dataStr) {
        try {
          frames.push({ event, data: JSON.parse(dataStr) });
        } catch {
          /* skip malformed */
        }
      }
    }
    return frames;
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    for (const frame of parse(decoder.decode(value, { stream: true }))) onFrame(frame);
  }
}
